import { Plan, PlanFeatures } from "../types";
import { Feature } from "../../featureAvailability";

export type FeatureFlagSectionId =
    | "dataUploadIngestion"
    | "annotationLabeling"
    | "datasetVersionManagement"
    | "trainingModels"
    | "deploymentInference"
    | "edgeDevices"
    | "workflowsAutomation"
    | "activeLearning"
    | "monitoringAnalytics"
    | "workspaceAdminAccessControl"
    | "billingSubscription"
    | "complianceSecurity";
type FeatureFlagControlType = "checkbox";
type FeatureFlagCategory = "product" | "behavior";

/*
 These feature flags work alongside `featureAvailability.ts` to determine if a feature is available on a plan.
 New features should be added to `featureAvailability.ts` INSTEAD of here.
 They can also be added here if they require a manual override via the admin panel.
*/

// Feature flags are not the same as the feature IDs in `featureAvailability.ts`. The FeatureFlag enum represents all toggle-able workspace settings, including but not limited to features.
export enum FeatureFlag {
    CreditSystem = "creditSystem",
    AdvancedApiKeys = "advancedApiKeys",
    AllowPrivateProjects = "allowPrivateProjects",
    AllowMultipleTrials = "allowMultipleTrials",
    ApiUpload = "apiUpload",
    EmailUpload = "emailUpload",
    VideoProcessing = "videoProcessing",
    HealthCheck = "healthCheck",
    OutsourcedAnnotations = "outsourcedAnnotations",
    AdvancedTransforms = "advancedTransforms",
    ExportVersion = "exportVersion",
    Rbac = "rbac",
    AnnotationReview = "annotationReview",
    WorkspaceInsights = "workspaceInsights",
    DeviceMonitoring = "deviceMonitoring",
    DeviceManagerV2 = "deviceManagerV2",
    DeviceStatusEvents = "deviceStatusEvents",
    ClassAttributes = "classAttributes",
    AdvancedLabelAssist = "advancedLabelAssist",
    CloudMlIntegrations = "cloudMlIntegrations",
    EmbedInMobileApps = "embedInMobileApps",
    OfflineMode = "offlineMode",
    VpcDeployment = "vpcDeployment",
    HighComplexitySmartPoly = "highComplexitySmartPoly",
    ModelEvaluation = "modelEvaluation",
    ActiveLearning = "activeLearning",
    ActiveLearningDefaultEnabled = "activeLearningDefaultEnabled",
    ModelMonitoring = "modelMonitoring",
    HipaaCompliance = "hipaaCompliance",
    SessionRecording = "sessionRecording",
    ManageSourceVideo = "manageSourceVideo",
    RefreshAnnotationsButton = "refreshAnnotationsButton",
    UnlimitedTimeDedicatedDeployments = "unlimitedTimeDedicatedDeployments",
    BillSelfHostedDeployments = "billSelfHostedDeployments",
    AllowCustomPythonDedicatedDeployments = "allowCustomPythonDedicatedDeployments",
    AllowCustomConfigDedicatedDeployments = "allowCustomConfigDedicatedDeployments",
    UiUploadWithBackend = "uiUploadWithBackend",
    BatchProcessing = "batchProcessing",
    IgnoreFirstFailedInvoicePaymentDate = "ignoreFirstFailedInvoicePaymentDate",
    UseProjectFoldersForWorkflows = "useProjectFoldersForWorkflows",
    FolderBilling = "folderBilling",
    RestrictTopLevelFolderCreation = "restrictTopLevelFolderCreation",
    FolderPermissions = "folderPermissions",
    BucketMirroring = "bucketMirroring",
    AccessLogs = "accessLogs",
    ModelEvaluationTrainSet = "modelEvaluationTrainSet",
    FilterByTags = "filterByTags",
    DisableAutoInstantModelTraining = "disableAutoInstantModelTraining",
    UserMetadata = "userMetadata",
    TrtOnDemandCompilation = "trtOnDemandCompilation",
    UnrestrictedEmailProxy = "unrestrictedEmailProxy",
    ExternalModelManagement = "externalModelManagement",
    WorkflowBlockControls = "workflowBlockControls",
    WorkflowsEditorV2Default = "workflowsEditorV2Default",
    CanDisablePublicProjects = "canDisablePublicProjects",
    ContinuousImprovement = "continuousImprovement",
    CreditUsageDashboard = "creditUsageDashboard",
    VisionEvents = "visionEvents"
}

export type FeatureFlagInformation = {
    id: FeatureFlag;
    name: string;
    description?: string;
    feature?: Feature;
    section: FeatureFlagSectionId;
    controlType: FeatureFlagControlType;
    order: number;
    defaultValueWhenMissing?: boolean;
    overwriteOnPlanChange: boolean;
    applyPlanChangeOverrideInAdmin?: boolean;
    category: FeatureFlagCategory;
};

export type FeatureFlagSectionInformation = {
    id: FeatureFlagSectionId;
    label: string;
    order: number;
};

export const featureFlagSectionInformation: Record<
    FeatureFlagSectionId,
    FeatureFlagSectionInformation
> = {
    dataUploadIngestion: { id: "dataUploadIngestion", label: "Data Upload & Ingestion", order: 10 },
    annotationLabeling: { id: "annotationLabeling", label: "Annotation & Labeling", order: 20 },
    datasetVersionManagement: {
        id: "datasetVersionManagement",
        label: "Dataset & Version Management",
        order: 30
    },
    trainingModels: { id: "trainingModels", label: "Training & Models", order: 40 },
    deploymentInference: { id: "deploymentInference", label: "Deployment & Inference", order: 50 },
    edgeDevices: { id: "edgeDevices", label: "Edge Devices", order: 60 },
    workflowsAutomation: { id: "workflowsAutomation", label: "Workflows & Automation", order: 70 },
    activeLearning: { id: "activeLearning", label: "Active Learning", order: 80 },
    monitoringAnalytics: { id: "monitoringAnalytics", label: "Monitoring & Analytics", order: 90 },
    workspaceAdminAccessControl: {
        id: "workspaceAdminAccessControl",
        label: "Workspace Administration & Access Control",
        order: 100
    },
    billingSubscription: { id: "billingSubscription", label: "Billing & Subscription", order: 110 },
    complianceSecurity: { id: "complianceSecurity", label: "Compliance & Security", order: 120 }
};

export const DEFAULT_FEATURES: PlanFeatures = {
    creditSystem: true,
    allowPrivateProjects: false,
    apiUpload: false,
    emailUpload: false,
    videoProcessing: false,
    healthCheck: false,
    outsourcedAnnotations: false,
    advancedTransforms: false,
    exportVersion: false,
    cloudMlIntegrations: false,
    tfjsInBrowser: "dev_only",
    edgeInference: "dev_only",
    embedInMobileApps: false,
    offlineMode: false,
    vpcDeployment: false,
    supportLevel: "None",
    annotationReview: false,
    workspaceInsights: false,
    classAttributes: false,
    advancedLabelAssist: false,
    deviceMonitoring: false,
    deviceManagerV2: false,
    rbac: false,
    highComplexitySmartPoly: false,
    modelEvaluation: false,
    activeLearning: false,
    modelMonitoring: false,
    hipaaCompliance: false,
    sessionRecording: false,
    manageSourceVideo: false,
    refreshAnnotationsButton: false,
    unlimitedTimeDedicatedDeployments: false,
    billSelfHostedDeployments: true,
    projectFolders: false,
    restrictTopLevelFolderCreation: false,
    advancedApiKeys: false,
    useProjectFoldersForWorkflows: true,
    disableAutoInstantModelTraining: false,
    folderBilling: false,
    userMetadata: false,
    bucketMirroring: false,
    unrestrictedEmailProxy: false,
    hideUpgradeCTA: false,
    workflowBlockControls: false,
    workflowsEditorV2Default: false,
    canDisablePublicProjects: false,
    creditUsageDashboard: false,
    visionEvents: false
};

export const PLAN_FEATURES: Record<Plan, Partial<PlanFeatures>> = {
    [Plan.Public]: {
        apiUpload: true,
        emailUpload: true,
        videoProcessing: true,
        healthCheck: true,
        exportVersion: true,
        tfjsInBrowser: "with_attribution",
        edgeInference: "with_attribution",
        embedInMobileApps: true
    },
    [Plan.UbpBasic]: {
        allowPrivateProjects: true,
        apiUpload: true,
        emailUpload: true,
        videoProcessing: true,
        healthCheck: true,
        advancedTransforms: true,
        exportVersion: true,
        cloudMlIntegrations: true,
        advancedLabelAssist: true,
        modelEvaluation: true,
        manageSourceVideo: true
    },
    [Plan.UbpCore]: {
        allowPrivateProjects: true,
        apiUpload: true,
        emailUpload: true,
        videoProcessing: true,
        healthCheck: true,
        advancedTransforms: true,
        exportVersion: true,
        cloudMlIntegrations: true,
        advancedLabelAssist: true,
        modelEvaluation: true,
        manageSourceVideo: true
    },
    [Plan.UbpGrowth]: {
        allowPrivateProjects: true,
        apiUpload: true,
        emailUpload: true,
        videoProcessing: true,
        healthCheck: true,
        advancedTransforms: true,
        exportVersion: true,
        cloudMlIntegrations: true,
        annotationReview: true,
        advancedLabelAssist: true,
        rbac: true,
        modelEvaluation: true,
        modelMonitoring: true,
        manageSourceVideo: true
    },
    [Plan.UbpPremiumTrial]: {
        allowPrivateProjects: true,
        apiUpload: true,
        emailUpload: true,
        videoProcessing: true,
        healthCheck: true,
        advancedTransforms: true,
        exportVersion: true,
        cloudMlIntegrations: true,
        advancedLabelAssist: true,
        rbac: true,
        modelEvaluation: true,
        modelMonitoring: true,
        manageSourceVideo: true
    },
    [Plan.EnterpriseGrowth]: {
        allowPrivateProjects: true,
        apiUpload: true,
        emailUpload: true,
        videoProcessing: true,
        healthCheck: true,
        advancedTransforms: true,
        exportVersion: true,
        cloudMlIntegrations: true,
        annotationReview: true,
        advancedLabelAssist: true,
        rbac: true,
        modelEvaluation: true,
        modelMonitoring: true,
        manageSourceVideo: true,
        deviceManagerV2: true,
        classAttributes: true,
        canDisablePublicProjects: true
    },
    [Plan.Enterprise]: {
        allowPrivateProjects: true,
        apiUpload: true,
        emailUpload: true,
        videoProcessing: true,
        healthCheck: true,
        advancedTransforms: true,
        exportVersion: true,
        cloudMlIntegrations: true,
        annotationReview: true,
        advancedLabelAssist: true,
        rbac: true,
        modelEvaluation: true,
        modelMonitoring: true,
        manageSourceVideo: true,
        folderPermissions: false, // Add-on: Enterprise Access Control & Data Governance
        deviceManagerV2: true,
        classAttributes: true,
        canDisablePublicProjects: true
    },
    [Plan.LegacySandbox]: {
        creditSystem: false,
        allowPrivateProjects: true,
        apiUpload: true,
        emailUpload: true,
        videoProcessing: true,
        edgeInference: "forbidden"
    },
    [Plan.LegacyStarter]: {
        creditSystem: false,
        allowPrivateProjects: true,
        apiUpload: true,
        emailUpload: true,
        videoProcessing: true,
        healthCheck: true,
        advancedTransforms: true,
        exportVersion: true,
        cloudMlIntegrations: true,
        edgeInference: "forbidden",
        modelEvaluation: true,
        manageSourceVideo: true,
        classAttributes: true
    },
    [Plan.Research]: {
        allowPrivateProjects: false,
        apiUpload: true,
        emailUpload: true,
        videoProcessing: true,
        healthCheck: true,
        advancedTransforms: true,
        exportVersion: true,
        tfjsInBrowser: "with_attribution",
        edgeInference: "with_attribution",
        embedInMobileApps: true
    },
    [Plan.UninitalizedWorkspace]: {}
} as any;

export const FEATURES_CONFIG: Record<Plan, PlanFeatures> = Object.keys(PLAN_FEATURES).reduce(
    (acc, plan) => {
        acc[plan] = { ...DEFAULT_FEATURES, ...PLAN_FEATURES[plan] };
        return acc;
    },
    {} as Record<Plan, PlanFeatures>
);

export const featureFlagInformation: Record<FeatureFlag, FeatureFlagInformation> = {
    [FeatureFlag.CreditSystem]: {
        id: FeatureFlag.CreditSystem,
        name: "Credit System",
        description: "Use the credit-based UBP billing system for usage and quotas.",
        section: "billingSubscription",
        controlType: "checkbox",
        order: 10,
        overwriteOnPlanChange: true,
        category: "behavior"
    },
    [FeatureFlag.AdvancedApiKeys]: {
        id: FeatureFlag.AdvancedApiKeys,
        name: "Advanced API Key Management",
        description:
            "Advanced API key management including scoped permissions, multiple keys, and key disabling.",
        section: "workspaceAdminAccessControl",
        controlType: "checkbox",
        order: 10,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.AllowPrivateProjects]: {
        id: FeatureFlag.AllowPrivateProjects,
        name: "Allow Private Projects",
        feature: Feature.PrivateProjects,
        section: "workspaceAdminAccessControl",
        controlType: "checkbox",
        order: 20,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.AllowMultipleTrials]: {
        id: FeatureFlag.AllowMultipleTrials,
        name: "Allow Multiple Trials",
        description:
            "Allows any user in this workspace to activate a Premium Trial even if they have previously used one. Useful for Roboflow internal testing and demos.",
        section: "billingSubscription",
        controlType: "checkbox",
        order: 30,
        overwriteOnPlanChange: true,
        category: "behavior"
    },
    [FeatureFlag.ApiUpload]: {
        id: FeatureFlag.ApiUpload,
        name: "API Upload",
        section: "dataUploadIngestion",
        controlType: "checkbox",
        order: 40,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.EmailUpload]: {
        id: FeatureFlag.EmailUpload,
        name: "Email Upload",
        section: "dataUploadIngestion",
        controlType: "checkbox",
        order: 50,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.VideoProcessing]: {
        id: FeatureFlag.VideoProcessing,
        name: "Video Processing",
        section: "dataUploadIngestion",
        controlType: "checkbox",
        order: 60,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.HealthCheck]: {
        id: FeatureFlag.HealthCheck,
        name: "Dataset Analytics",
        description: "Enable Dataset Analytics (Health Check) for projects.",
        section: "datasetVersionManagement",
        controlType: "checkbox",
        order: 70,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.OutsourcedAnnotations]: {
        id: FeatureFlag.OutsourcedAnnotations,
        name: "Outsource Annotations",
        section: "annotationLabeling",
        controlType: "checkbox",
        order: 90,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.AdvancedTransforms]: {
        id: FeatureFlag.AdvancedTransforms,
        name: "Advanced Transforms",
        description:
            "Unlock advanced augmentation transforms (motion blur, camera gain, cutout, hue, saturation, etc.) and preprocessing options (contrast types, static crop, tile, isolate, remap, filter-tags).",
        section: "datasetVersionManagement",
        controlType: "checkbox",
        order: 100,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.ExportVersion]: {
        id: FeatureFlag.ExportVersion,
        name: "Export Version",
        section: "datasetVersionManagement",
        controlType: "checkbox",
        order: 110,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.Rbac]: {
        id: FeatureFlag.Rbac,
        name: "RBAC (Labeler Role)",
        description:
            "Enable role-based access control with labeler and reviewer roles, custom roles, and fine-grained permission management for workspace members.",
        section: "workspaceAdminAccessControl",
        controlType: "checkbox",
        order: 120,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.AnnotationReview]: {
        id: FeatureFlag.AnnotationReview,
        name: "Annotation Review",
        feature: Feature.AnnotationReview,
        section: "annotationLabeling",
        controlType: "checkbox",
        order: 130,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.WorkspaceInsights]: {
        id: FeatureFlag.WorkspaceInsights,
        name: "Workspace Insights",
        description:
            "Workspace Insights page showing annotation analytics: images labeled, annotations added/removed/updated, per-labeler breakdowns, etc.",
        section: "monitoringAnalytics",
        controlType: "checkbox",
        order: 140,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.DeviceMonitoring]: {
        id: FeatureFlag.DeviceMonitoring,
        name: "Device Monitoring",
        description:
            "Enable device monitoring page for managing edge devices, viewing device telemetry, and monitoring deployments.",
        section: "edgeDevices",
        controlType: "checkbox",
        order: 150,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.DeviceManagerV2]: {
        id: FeatureFlag.DeviceManagerV2,
        name: "Device Manager V2",
        section: "edgeDevices",
        controlType: "checkbox",
        order: 160,
        overwriteOnPlanChange: true,
        applyPlanChangeOverrideInAdmin: true,
        category: "product"
    },
    [FeatureFlag.DeviceStatusEvents]: {
        id: FeatureFlag.DeviceStatusEvents,
        name: "Device Status Events Beta",
        description:
            "Use structured device status events for tracking device, stream, and container lifecycle events (online/offline, started/stopped, errors) with detailed metadata.",
        section: "edgeDevices",
        controlType: "checkbox",
        order: 165,
        overwriteOnPlanChange: true,
        category: "behavior"
    },
    [FeatureFlag.ClassAttributes]: {
        id: FeatureFlag.ClassAttributes,
        name: "Class Attributes",
        description:
            "Enable class-level attributes to be attached to individual annotations for custom use cases. Models don't train on these.",
        section: "annotationLabeling",
        controlType: "checkbox",
        order: 170,
        overwriteOnPlanChange: true,
        applyPlanChangeOverrideInAdmin: true,
        category: "product"
    },
    [FeatureFlag.AdvancedLabelAssist]: {
        id: FeatureFlag.AdvancedLabelAssist,
        name: "Advanced Label Assist",
        description:
            "Enable Label Assist (model predictions), Box Prompting (visual prompting), Smart Select/Smart Polygon (segmentation), and Auto Label.",
        section: "annotationLabeling",
        controlType: "checkbox",
        order: 180,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.CloudMlIntegrations]: {
        id: FeatureFlag.CloudMlIntegrations,
        name: "Cloud ML Integrations",
        description: "Allow exports to cloud AutoML integrations (AWS, Google, Azure).",
        section: "deploymentInference",
        controlType: "checkbox",
        order: 190,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.EmbedInMobileApps]: {
        id: FeatureFlag.EmbedInMobileApps,
        name: "Embed In Mobile Apps",
        section: "deploymentInference",
        controlType: "checkbox",
        order: 200,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.OfflineMode]: {
        id: FeatureFlag.OfflineMode,
        name: "Offline Mode",
        description:
            "Allow long-term caching of TensorflowJS (web browser) models for offline use.",
        section: "deploymentInference",
        controlType: "checkbox",
        order: 210,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.VpcDeployment]: {
        id: FeatureFlag.VpcDeployment,
        name: "VPC Deployment",
        section: "deploymentInference",
        controlType: "checkbox",
        order: 220,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.HighComplexitySmartPoly]: {
        id: FeatureFlag.HighComplexitySmartPoly,
        name: "High Complexity Smart Poly",
        description:
            "Enable higher-complexity Smart Polygon settings by lowering the simplification threshold minimum, allowing 'Most Complex' option in the UI slider for more detailed polygon annotations.",
        section: "annotationLabeling",
        controlType: "checkbox",
        order: 230,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.ModelEvaluation]: {
        id: FeatureFlag.ModelEvaluation,
        name: "Model Evaluation",
        feature: Feature.ModelEvaluation,
        section: "trainingModels",
        controlType: "checkbox",
        order: 240,
        overwriteOnPlanChange: true,
        applyPlanChangeOverrideInAdmin: true,
        category: "product"
    },
    [FeatureFlag.ActiveLearning]: {
        id: FeatureFlag.ActiveLearning,
        name: "Active Learning",
        section: "activeLearning",
        controlType: "checkbox",
        order: 250,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.ActiveLearningDefaultEnabled]: {
        id: FeatureFlag.ActiveLearningDefaultEnabled,
        name: "Active Learning Active for All New Projects (API Only)",
        description: "Automatically add default Active Learning config for API-created datasets.",
        section: "activeLearning",
        controlType: "checkbox",
        order: 260,
        overwriteOnPlanChange: true,
        category: "behavior"
    },
    [FeatureFlag.ModelMonitoring]: {
        id: FeatureFlag.ModelMonitoring,
        name: "Model Monitoring",
        feature: Feature.ModelMonitoring,
        section: "monitoringAnalytics",
        controlType: "checkbox",
        order: 280,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.HipaaCompliance]: {
        id: FeatureFlag.HipaaCompliance,
        name: "HIPAA Compliance",
        description:
            "Enforce HIPAA compliance restrictions by preventing Roboflow support members (help@roboflow.com) from being added to the workspace. Does not prevent workspace access through adding users in the admin panel.",
        section: "complianceSecurity",
        controlType: "checkbox",
        order: 290,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.SessionRecording]: {
        id: FeatureFlag.SessionRecording,
        name: "Session Recording (OpenReplay)",
        description: "Force-enable OpenReplay session capture for this workspace.",
        section: "monitoringAnalytics",
        controlType: "checkbox",
        order: 300,
        overwriteOnPlanChange: true,
        category: "behavior"
    },
    [FeatureFlag.ManageSourceVideo]: {
        id: FeatureFlag.ManageSourceVideo,
        name: "Manage Source Video",
        description:
            "Allow managing whole source videos, not just the extracted frames in the dataset.",
        section: "dataUploadIngestion",
        controlType: "checkbox",
        order: 310,
        overwriteOnPlanChange: true,
        applyPlanChangeOverrideInAdmin: true,
        category: "product"
    },
    [FeatureFlag.RefreshAnnotationsButton]: {
        id: FeatureFlag.RefreshAnnotationsButton,
        name: "Refresh Annotations Button",
        description: "Show the Refresh button in Annotation Board.",
        section: "annotationLabeling",
        controlType: "checkbox",
        order: 320,
        overwriteOnPlanChange: true,
        category: "behavior"
    },
    [FeatureFlag.UnlimitedTimeDedicatedDeployments]: {
        id: FeatureFlag.UnlimitedTimeDedicatedDeployments,
        name: "Dedicated Deployments Run Forever",
        section: "deploymentInference",
        controlType: "checkbox",
        order: 330,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.BillSelfHostedDeployments]: {
        id: FeatureFlag.BillSelfHostedDeployments,
        name: "Bill Self-Hosted Deployments",
        description:
            "Controls whether self-hosted inference usage should be billed. Older workspaces may be grandfathered with this off.",
        section: "billingSubscription",
        controlType: "checkbox",
        order: 340,
        overwriteOnPlanChange: false,
        category: "behavior"
    },
    [FeatureFlag.AllowCustomPythonDedicatedDeployments]: {
        id: FeatureFlag.AllowCustomPythonDedicatedDeployments,
        name: "Dedicated Deployments Allow Custom Python",
        section: "deploymentInference",
        controlType: "checkbox",
        order: 350,
        defaultValueWhenMissing: true,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.AllowCustomConfigDedicatedDeployments]: {
        id: FeatureFlag.AllowCustomConfigDedicatedDeployments,
        name: "Dedicated Deployments Allow Custom Config",
        section: "deploymentInference",
        controlType: "checkbox",
        order: 360,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.UiUploadWithBackend]: {
        id: FeatureFlag.UiUploadWithBackend,
        name: "UI Upload with Backend",
        description:
            "Route UI uploads through the backend upload flow for supported project types.",
        section: "dataUploadIngestion",
        controlType: "checkbox",
        order: 370,
        overwriteOnPlanChange: true,
        category: "behavior"
    },
    [FeatureFlag.BatchProcessing]: {
        id: FeatureFlag.BatchProcessing,
        name: "Batch Processing",
        section: "deploymentInference",
        controlType: "checkbox",
        order: 380,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.IgnoreFirstFailedInvoicePaymentDate]: {
        id: FeatureFlag.IgnoreFirstFailedInvoicePaymentDate,
        name: "Ignore First Failed Invoice Payment Date",
        description: "Ignore the first failed invoice date before gating workspace access.",
        section: "billingSubscription",
        controlType: "checkbox",
        order: 390,
        overwriteOnPlanChange: true,
        category: "behavior"
    },
    [FeatureFlag.UseProjectFoldersForWorkflows]: {
        id: FeatureFlag.UseProjectFoldersForWorkflows,
        name: "Unified Project/Workflow Folders",
        description: "Uses a single folder structure for projects and workflows.",
        section: "workflowsAutomation",
        controlType: "checkbox",
        order: 400,
        overwriteOnPlanChange: true,
        category: "behavior"
    },
    [FeatureFlag.FolderBilling]: {
        id: FeatureFlag.FolderBilling,
        name: "Billing Folders",
        description: "Enable folder-based billing and usage attribution.",
        feature: Feature.FolderBilling,
        section: "billingSubscription",
        controlType: "checkbox",
        order: 410,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.RestrictTopLevelFolderCreation]: {
        id: FeatureFlag.RestrictTopLevelFolderCreation,
        name: "Restrict Top-Level Folder Creation (Admins Only)",
        description: "Restrict top-level folder creation to users with permission.",
        section: "workspaceAdminAccessControl",
        controlType: "checkbox",
        order: 415,
        overwriteOnPlanChange: true,
        category: "behavior"
    },
    [FeatureFlag.FolderPermissions]: {
        id: FeatureFlag.FolderPermissions,
        name: "Folder Permissions",
        description:
            "Restrict project and workflow folder access by role and permissions, enabling SSO group-based and user-level read/write access controls.",
        feature: Feature.FolderPermissions,
        section: "workspaceAdminAccessControl",
        controlType: "checkbox",
        order: 420,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.BucketMirroring]: {
        id: FeatureFlag.BucketMirroring,
        name: "Bucket Mirroring",
        description:
            "Enable S3 bucket mirroring to automatically import images from customer S3 buckets into the workspace asset library, and keep orphaned sources (files removed from bucket) instead of deleting them.",
        feature: Feature.BucketMirroring,
        section: "dataUploadIngestion",
        controlType: "checkbox",
        order: 430,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.AccessLogs]: {
        id: FeatureFlag.AccessLogs,
        name: "Access Logs",
        feature: Feature.AccessLogs,
        section: "monitoringAnalytics",
        controlType: "checkbox",
        order: 440,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.ModelEvaluationTrainSet]: {
        id: FeatureFlag.ModelEvaluationTrainSet,
        name: "Model Evaluation Train Set",
        description:
            "Allow model evaluation to include the training set. Generally excluded since training split data is what the model trained on.",
        section: "trainingModels",
        controlType: "checkbox",
        order: 450,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.FilterByTags]: {
        id: FeatureFlag.FilterByTags,
        name: "Filter By Tags",
        description: "Allow model evaluation to filter by tags.",
        section: "datasetVersionManagement",
        controlType: "checkbox",
        order: 460,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.DisableAutoInstantModelTraining]: {
        id: FeatureFlag.DisableAutoInstantModelTraining,
        name: "Disable Auto Instant Model Training",
        description:
            "Disable auto-triggered instant model training. Enabling this flag DISABLES the RF Instant model trigger.",
        section: "trainingModels",
        controlType: "checkbox",
        order: 470,
        overwriteOnPlanChange: true,
        category: "behavior"
    },
    [FeatureFlag.UserMetadata]: {
        id: FeatureFlag.UserMetadata,
        name: "User Metadata",
        description: "Allow editing and adding custom image metadata in tag and bulk-edit tools.",
        section: "datasetVersionManagement",
        controlType: "checkbox",
        order: 490,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.TrtOnDemandCompilation]: {
        id: FeatureFlag.TrtOnDemandCompilation,
        name: "TRT On Demand Compilation",
        description: "Allow on-demand TensorRT compilation jobs.",
        section: "deploymentInference",
        controlType: "checkbox",
        order: 500,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.UnrestrictedEmailProxy]: {
        id: FeatureFlag.UnrestrictedEmailProxy,
        name: "Unrestricted Email Proxy",
        description: "Allow sending emails to non-workspace members via the email proxy.",
        section: "workspaceAdminAccessControl",
        controlType: "checkbox",
        order: 510,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.ExternalModelManagement]: {
        id: FeatureFlag.ExternalModelManagement,
        name: "External Model Management",
        description:
            "Enable external APIs for registering and managing model packages (weights, manifests, metadata) that can be uploaded and hosted outside the standard Roboflow training pipeline.",
        section: "trainingModels",
        controlType: "checkbox",
        order: 520,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.WorkflowBlockControls]: {
        id: FeatureFlag.WorkflowBlockControls,
        name: "Workspace Block Controls",
        description:
            "Enables customers to control which workflow blocks are enabled in their workspace.",
        section: "workflowsAutomation",
        controlType: "checkbox",
        order: 530,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.WorkflowsEditorV2Default]: {
        id: FeatureFlag.WorkflowsEditorV2Default,
        name: "Workflows Editor V2 Default",
        description: "When enabled, the V2 workflow editor is used by default instead of V1.",
        section: "workflowsAutomation",
        controlType: "checkbox",
        order: 535,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.CanDisablePublicProjects]: {
        id: FeatureFlag.CanDisablePublicProjects,
        name: "Can Disable Public Projects",
        description: "When enabled, workspace can choose to disable public project creation.",
        feature: Feature.CanDisablePublicProjects,
        section: "workspaceAdminAccessControl",
        controlType: "checkbox",
        order: 540,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.ContinuousImprovement]: {
        id: FeatureFlag.ContinuousImprovement,
        name: "Continuous Improvement (Rapid-only for now)",
        description: "Enables automatic model retraining - currently gated for Rapid models only.",
        section: "trainingModels",
        controlType: "checkbox",
        order: 550,
        overwriteOnPlanChange: true,
        category: "product"
    },
    [FeatureFlag.CreditUsageDashboard]: {
        id: FeatureFlag.CreditUsageDashboard,
        name: "Credit Usage Dashboard",
        description:
            "Enable the enhanced credit usage dashboard with daily usage charts, projections, and tips on the workspace Credit Usage page.",
        section: "billingSubscription",
        controlType: "checkbox",
        order: 420,
        overwriteOnPlanChange: true,
        category: "behavior"
    },
    [FeatureFlag.VisionEvents]: {
        id: FeatureFlag.VisionEvents,
        name: "Vision Events",
        description:
            "Enable vision events ingestion, querying, and visualization for this workspace.",
        feature: Feature.VisionEvents,
        section: "monitoringAnalytics",
        controlType: "checkbox",
        order: 460,
        overwriteOnPlanChange: false,
        category: "product"
    }
};

export function getFeatureFlagValue(
    flagId: string,
    usage: { features?: Record<string, any>; plan?: Plan | string | null }
): boolean {
    return !!usage?.features?.[flagId];
}
